import { derived, writable } from "svelte/store";
import type { PersistedAddress } from "../../lib/api/address";
import { get } from "svelte/store";

type AddressMode = "view" | "create" | "select";

export type AddressesStore = {
  addresses: Array<PersistedAddress>;
  addressForEdit?: PersistedAddress;
  mode: AddressMode;
  hasLoaded: boolean;
  addressInvalid: boolean;
  noAddressesFound: boolean;
  enterAddressManually: boolean;
};

export const updateAddressesStore = (partialAddresses: Partial<AddressesStore>) => {
  addressesStore.update((data) => ({ ...data, ...partialAddresses }));
};

export function setAddresses(addresses: Array<PersistedAddress>) {
  updateAddressesStore({
    addresses,
    mode: addresses.length === 0 ? "create" : "view",
    hasLoaded: true,
    noAddressesFound: false,
    enterAddressManually: false
  });
}

export function setMode(mode: AddressMode) {
  updateAddressesStore({ mode });
}

export function setSelectedAddress(id: number) {
  updateAddressesStore({
    addresses: get(addressesStore).addresses.map((address) => ({ ...address, is_default: address.id === id }))
  });
}

export function setAddressForEdit(address: PersistedAddress) {
  updateAddressesStore({
    addressForEdit: address,
    mode: "create"
  });
}

export function unsetAddressForEdit() {
  updateAddressesStore({ addressForEdit: undefined });
}

export function addNewAddress(address: PersistedAddress) {
  updateAddressesStore({
    addresses: [
      ...get(addressesStore).addresses.map((address) => ({ ...address, is_default: false })),
      { ...address, is_default: true }
    ],
    mode: "view"
  });
}

export function updateAddress(address: PersistedAddress) {
  updateAddressesStore({
    addresses: [
      ...get(addressesStore).addresses.filter((a) => a.id !== address.id).map((a) => ({ ...a, is_default: false })),
      { ...address, is_default: true }
    ],
    mode: "view"
  });
}

export function initStore() {
  const store = writable<AddressesStore>();
  store.set({
    addresses: [],
    mode: "create",
    hasLoaded: false,
    addressInvalid: false,
    noAddressesFound: false,
    enterAddressManually: false
  });
  return store;
}

export const addressesStore = initStore();

export const selectedAddressStore = derived(addressesStore, ($addresses) => {
  return $addresses.addresses.find((address) => address.is_default);
});
